import {
  type MenuSelectFilterChange,
  MenuSelectFilterInput,
  type SelectedCell,
  type SortCriteria,
  Table,
  TableCell,
  type TextFilterChange,
  TextFilterInput
} from "@relex-solutions/fragments/Table"
import { useTableManager } from "@relex-solutions/fragments/TableManager"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { CUSTOMER_COLUMN_IDS } from "@/domains/customers/constants/customerTableColumns"
import { useCustomerQueryState } from "@/domains/customers/hooks/useCustomerQueryState"
import { useGetCustomers } from "@/domains/customers/hooks/useGetCustomers"
import type { Customer } from "@/domains/customers/types/Customer"
import { copyToClipboardAsCSV } from "@/domains/customers/utils/clipboard"
import { getSelectedValuesWithHeaders } from "@/domains/customers/utils/tableDataHelpers"
import { ErrorComponent } from "@/shared/components/ErrorComponent/ErrorComponent"
import { Loading } from "@/shared/components/Loading/Loading"
import { CUSTOMERS_BASE_PATH } from "@/shared/constants/routePaths"
import type { SortOrder } from "@/shared/types/SortOrder"
import { TextFilterOperators } from "./CustomerListContent.types"
import { getCustomerListQueryParamUpdates } from "./CustomerListContent.utils"

const DEFAULT_PAGE = 0

const getCustomerURL = (customerId: string) => `${CUSTOMERS_BASE_PATH}/${customerId}/overview`

export function CustomerListContent() {
  const {
    page,
    pageSize,
    searchTerm,
    sortBy,
    sortOrder,
    updateParams,
    salesforceId,
    relationshipType,
    relationshipSubType,
    sdmEmail
  } = useCustomerQueryState()

  const query = useGetCustomers({
    page,
    pageSize,
    searchTerm,
    sortBy,
    sortOrder: sortOrder || "ASC",
    sdmEmail,
    salesforceId,
    relationshipType,
    relationshipSubType
  })

  const handleSortChange = (sortBy: string | undefined, newSortOrder: SortOrder | undefined) => {
    updateParams({ sortBy, sortOrder: newSortOrder })
  }

  const handlePageSizeChange = (newPageSize: number) => {
    updateParams({ pageSize: newPageSize, page: Math.floor((page * pageSize) / newPageSize) })
  }

  const handlePageChange = (page: number) => {
    updateParams({ page })
  }

  type Filter = TextFilterChange | MenuSelectFilterChange<string>

  // Use ref to keep updateParams stable and avoid recreating onFilterChanged
  const updateParamsRef = useRef(updateParams)
  updateParamsRef.current = updateParams

  // Stable callback that doesn't change when updateParams changes
  // This prevents column recreation and focus loss during typing
  const onFilterChanged = useCallback((change: Filter) => {
    updateParamsRef.current(getCustomerListQueryParamUpdates(change))
  }, [])

  const { meta, data: customers } = query.data ?? {
    meta: { page: DEFAULT_PAGE, total: 0, pageSize: 20 },
    data: []
  }

  const navigate = useNavigate()
  const { t } = useTranslation()
  const [selectedCells, setSelectedCells] = useState<SelectedCell<Customer>[]>([])

  const handleCellSelectionChanged = useCallback((newSelectedCells: (Customer & SelectedCell<Customer>)[]) => {
    setSelectedCells(newSelectedCells)
  }, [])

  const handleCopy = useCallback(() => {
    const selectedValues = getSelectedValuesWithHeaders(customers, selectedCells, t)
    copyToClipboardAsCSV(selectedValues)
  }, [selectedCells, customers, t])

  // Create columns with the filters
  const { columns, onResizeColumnCompleted } = useTableManager<Customer>(
    () => [
      {
        key: CUSTOMER_COLUMN_IDS.NAME,
        title: t("customers.table.name"),
        sortable: true,
        id: CUSTOMER_COLUMN_IDS.NAME,
        display: ({ value, record }) => (
          <TableCell>
            <a
              className="fr-link"
              href={getCustomerURL(record.id)}
              target="_blank"
              rel="noreferrer"
              onClick={(ev) => {
                ev.preventDefault()
                navigate(getCustomerURL(record.id))
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault()
                  navigate(getCustomerURL(record.id))
                }
              }}
            >
              {value}
            </a>
          </TableCell>
        ),
        filter: (
          <TextFilterInput
            id={CUSTOMER_COLUMN_IDS.NAME}
            onFilterChanged={onFilterChanged}
            initialValue={searchTerm ? { operator: TextFilterOperators.Contains, value: searchTerm } : undefined}
            disabledOperators={[
              TextFilterOperators.Empty,
              TextFilterOperators.NotEmpty,
              TextFilterOperators.NotContains
            ]}
          />
        )
      },
      {
        key: CUSTOMER_COLUMN_IDS.SALESFORCE_ID,
        title: t("customers.table.salesforceId"),
        sortable: true,
        id: CUSTOMER_COLUMN_IDS.SALESFORCE_ID,
        filter: (
          <TextFilterInput
            id={CUSTOMER_COLUMN_IDS.SALESFORCE_ID}
            onFilterChanged={onFilterChanged}
            initialValue={salesforceId ? { operator: TextFilterOperators.Contains, value: salesforceId } : undefined}
            disabledOperators={[
              TextFilterOperators.Empty,
              TextFilterOperators.NotEmpty,
              TextFilterOperators.NotContains
            ]}
          />
        )
      },

      {
        key: CUSTOMER_COLUMN_IDS.RELATIONSHIP_TYPE,
        title: t("customers.table.relationshipType"),
        sortable: false,
        id: CUSTOMER_COLUMN_IDS.RELATIONSHIP_TYPE,
        filter: (
          <MenuSelectFilterInput
            id={CUSTOMER_COLUMN_IDS.RELATIONSHIP_TYPE}
            single
            onFilterChanged={onFilterChanged}
            initialValue={relationshipType ? [relationshipType] : undefined}
            options={[
              { value: "Customer", label: t("customers.table.customer") },
              { value: "FormerCustomer", label: t("customers.table.formerCustomer") }
            ]}
          />
        )
      },
      {
        key: CUSTOMER_COLUMN_IDS.SUB_TYPE,
        title: t("customers.table.subType"),
        sortable: false,
        id: CUSTOMER_COLUMN_IDS.SUB_TYPE,
        filter: (
          <MenuSelectFilterInput
            id={CUSTOMER_COLUMN_IDS.SUB_TYPE}
            single
            onFilterChanged={onFilterChanged}
            initialValue={relationshipSubType ? [relationshipSubType] : undefined}
            options={[
              { value: "Customer", label: t("customers.table.customer") },
              { value: "BillingEntity", label: t("customers.table.billingEntity") },
              { value: "CustomersAffiliate", label: t("customers.table.customersAffiliate") }
            ]}
          />
        )
      },
      {
        key: CUSTOMER_COLUMN_IDS.SDM_EMAIL,
        title: t("customers.table.sdmEmail"),
        sortable: true,
        id: CUSTOMER_COLUMN_IDS.SDM_EMAIL,
        filter: (
          <TextFilterInput
            id={CUSTOMER_COLUMN_IDS.SDM_EMAIL}
            onFilterChanged={onFilterChanged}
            initialValue={sdmEmail ? { operator: TextFilterOperators.Contains, value: sdmEmail } : undefined}
            disabledOperators={[
              TextFilterOperators.Empty,
              TextFilterOperators.NotEmpty,
              TextFilterOperators.NotContains
            ]}
          />
        )
      },
      {
        key: CUSTOMER_COLUMN_IDS.ID,
        title: t("customers.table.id"),
        sortable: true,
        id: CUSTOMER_COLUMN_IDS.ID
      },
      {
        key: CUSTOMER_COLUMN_IDS.TAX_NUMBER,
        title: t("customers.table.taxNumber"),
        sortable: true,
        id: CUSTOMER_COLUMN_IDS.TAX_NUMBER
      }
    ],
    [t, onFilterChanged]
  )

  if (query.isLoading) {
    return <Loading text={t("customers.loading.text")} />
  }

  if (query.isError) {
    return (
      <ErrorComponent
        error={query.error}
        onRetry={(): void => {
          void query.refetch()
        }}
      />
    )
  }

  return (
    <Table
      columns={columns}
      onResizeColumnWidthCompleted={onResizeColumnCompleted}
      data={customers}
      primaryKey="id"
      cellSelection="multiple"
      selectedCells={selectedCells}
      onCellSelectionChanged={handleCellSelectionChanged}
      onCopy={handleCopy}
      pagination={{
        onPageIndexChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
        pageIndex: meta.page,
        pageSize: meta.pageSize,
        pageSizeOptions: [10, 20, 50],
        totalRows: meta.total
      }}
      sort={{
        onSortChanged: (change: SortCriteria<Customer> | SortCriteria<Customer>[]) => {
          if (!Array.isArray(change)) {
            const newSortOrder = change?.isAscending === undefined ? undefined : change?.isAscending ? "ASC" : "DESC"
            handleSortChange(change?.prop, newSortOrder)
          }
        }
      }}
    />
  )
}
